import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Star, CheckCircle } from 'lucide-react';
import { ProductReview } from '../../types';

interface ReviewLightboxModalProps {
    reviews: ProductReview[];
    initialIndex: number;
    isOpen: boolean;
    onClose: () => void;
}

export const ReviewLightboxModal: React.FC<ReviewLightboxModalProps> = ({ 
    reviews, 
    initialIndex, 
    isOpen, 
    onClose 
}) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [activeMediaIndex, setActiveMediaIndex] = useState(0);

    useEffect(() => {
        setCurrentIndex(initialIndex);
        setActiveMediaIndex(0);
    }, [initialIndex]);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }

        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [isOpen]);

    if (!isOpen || reviews.length === 0) return null;

    const currentReview = reviews[currentIndex];
    const hasMedia = currentReview.media && currentReview.media.length > 0;

    const handleNext = () => {
        setCurrentIndex((prev) => (prev + 1) % reviews.length);
        setActiveMediaIndex(0);
    };

    const handlePrev = () => {
        setCurrentIndex((prev) => (prev - 1 + reviews.length) % reviews.length);
        setActiveMediaIndex(0);
    };

    const formatDate = (date: string) => {
        return new Date(date).toLocaleDateString('es-SV', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-10 bg-black/95 backdrop-blur-xl"
                onClick={onClose}
            >
                {/* Close Button */}
                <button 
                    onClick={onClose}
                    className="absolute top-4 right-4 md:top-10 md:right-10 text-zinc-500 hover:text-white transition-colors z-[110]"
                >
                    <X size={32} />
                </button>

                {/* Navigation Buttons - Desktop */}
                <button 
                    onClick={(e) => { e.stopPropagation(); handlePrev(); }}
                    className="absolute left-4 md:left-10 top-1/2 -translate-y-1/2 p-3 bg-white/5 hover:bg-white/10 rounded-full text-white transition-all z-[110] hidden md:block"
                >
                    <ChevronLeft size={32} />
                </button>
                <button 
                    onClick={(e) => { e.stopPropagation(); handleNext(); }}
                    className="absolute right-4 md:right-10 top-1/2 -translate-y-1/2 p-3 bg-white/5 hover:bg-white/10 rounded-full text-white transition-all z-[110] hidden md:block"
                >
                    <ChevronRight size={32} />
                </button>

                {/* Modal Content */}
                <motion.div 
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="bg-zinc-950 w-full max-w-6xl max-h-full overflow-hidden flex flex-col md:flex-row relative shadow-2xl border border-zinc-900"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Left Side: Media / Image */}
                    <div className="w-full md:w-[60%] bg-black flex items-center justify-center relative overflow-hidden min-h-[300px] md:min-h-0 group/media">
                        {hasMedia ? (
                            <img 
                                src={currentReview.media![activeMediaIndex].media_url} 
                                alt="Review" 
                                className="w-full h-full object-contain"
                            />
                        ) : (
                            <div className="flex flex-col items-center gap-4 text-zinc-800">
                                <Star size={64} className="opacity-10" />
                                <span className="text-xs font-mono uppercase tracking-widest">Sin fotografía</span>
                            </div>
                        )}

                        {/* Internal Media Thumbnails */}
                        {currentReview.media && currentReview.media.length > 1 && (
                            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 p-2 bg-black/40 backdrop-blur-md rounded-md border border-white/10">
                                {currentReview.media.map((m, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => setActiveMediaIndex(idx)}
                                        className={`w-12 h-12 rounded-sm overflow-hidden border-2 transition-all ${
                                            activeMediaIndex === idx ? 'border-white scale-110' : 'border-transparent opacity-50 hover:opacity-100'
                                        }`}
                                    >
                                        <img src={m.media_url} alt="Thumbnail" className="w-full h-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Mobile Navigation Overlays */}
                        <div className="absolute inset-0 md:hidden flex justify-between items-center px-4">
                             <button onClick={handlePrev} className="p-2 bg-black/20 rounded-full text-white"><ChevronLeft size={24}/></button>
                             <button onClick={handleNext} className="p-2 bg-black/20 rounded-full text-white"><ChevronRight size={24}/></button>
                        </div>
                    </div>

                    {/* Right Side: Details */}
                    <div className="w-full md:w-[40%] bg-zinc-950 p-8 md:p-12 flex flex-col justify-center border-l border-zinc-900">
                        <div className="mb-8">
                            <div className="flex items-start justify-between mb-8 gap-4">
                                <div className="flex gap-4 min-w-0">
                                    {/* Avatar */}
                                    <div className="flex-shrink-0 w-12 h-12 bg-zinc-900 rounded-full flex items-center justify-center font-bold text-white border border-zinc-800">
                                        {currentReview.customer_name.charAt(0).toUpperCase()}
                                    </div>
                                    
                                    {/* Name & Stars */}
                                    <div className="flex flex-col min-w-0">
                                        <div className="flex items-center gap-2 mb-1.5">
                                            <h4 className="text-lg font-bold text-white uppercase truncate leading-none">
                                                {currentReview.customer_name}
                                            </h4>
                                            <CheckCircle size={16} className="flex-shrink-0 text-green-500 fill-green-500/10" />
                                        </div>
                                        <div className="flex gap-0.5">
                                            {[...Array(5)].map((_, i) => (
                                                <Star 
                                                    key={i} 
                                                    size={12} 
                                                    className={i < currentReview.rating ? "fill-white text-white" : "text-zinc-800"} 
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Date */}
                                <div className="flex-shrink-0 text-right pt-1">
                                    <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest whitespace-nowrap">
                                        {formatDate(currentReview.created_at)}
                                    </span>
                                </div>
                            </div>

                            <p className="text-zinc-300 text-lg md:text-xl leading-relaxed italic font-medium">
                                "{currentReview.comment || 'El cliente no dejó comentarios, pero calificó su experiencia.'}"
                            </p>
                        </div>

                        <div className="pt-8 border-t border-zinc-900">
                            <div className="flex items-center gap-4">
                                <img src="/CCSV_LOGO.svg" alt="Logo" className="h-4 opacity-40" />
                                <span className="text-[9px] font-mono text-zinc-600 uppercase tracking-widest">Compra Verificada • Carbon Case SV</span>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};
